import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./MessageActionMenu.module.css";

export type MessageAction = {
  id: string;
  label: string;
  onSelect: () => void | Promise<void>;
  destructive?: boolean;
  disabled?: boolean;
};

export type MessageActionMenuProps = {
  open: boolean;
  anchorRect: DOMRect | null;
  actions: MessageAction[];
  onClose: () => void;
};

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 10;

const MessageActionMenu: React.FC<MessageActionMenuProps> = ({
  open,
  anchorRect,
  actions,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(
    null
  );

  useLayoutEffect(() => {
    if (!open || !anchorRect || !menuRef.current) {
      setPosition(null);
      return;
    }
    const menu = menuRef.current;
    const menuRect = menu.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Prefer directly below the message bubble (content box).
    let top = anchorRect.bottom + ANCHOR_GAP;
    top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(top, vh - menuRect.height - VIEWPORT_MARGIN)
    );

    // Horizontally centered under the anchor; clamp to viewport.
    let left =
      anchorRect.left + (anchorRect.width - menuRect.width) / 2;
    left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(left, vw - menuRect.width - VIEWPORT_MARGIN)
    );

    setPosition({ top, left });
  }, [open, anchorRect]);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: Event) => {
      const target = e.target as Node | null;
      if (menuRef.current && target && !menuRef.current.contains(target)) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleScroll = () => onClose();

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer, { passive: true });
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [open, onClose]);

  if (!open || !anchorRect) return null;

  const style: React.CSSProperties = position
    ? { top: position.top, left: position.left }
    : { top: -9999, left: -9999, visibility: "hidden" };

  return createPortal(
    <div
      ref={menuRef}
      className={styles.menu}
      style={style}
      role="menu"
      onContextMenu={(e) => e.preventDefault()}
    >
      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          role="menuitem"
          disabled={action.disabled}
          className={`${styles.item}${
            action.destructive ? ` ${styles.destructive}` : ""
          }`}
          onClick={async () => {
            if (action.disabled) return;
            try {
              await action.onSelect();
            } finally {
              onClose();
            }
          }}
        >
          {action.label}
        </button>
      ))}
    </div>,
    document.body
  );
};

export default MessageActionMenu;
